import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import useAppConfirm from './useAppConfirm';
import { useTableSort } from './useTableSort';
import { useKkpBatValideFlow } from './useKkpBatValideFlow';
import { useSetAssistantPageClient } from '../context/AssistantPageClientContext';
import { mergePlanningRow } from '../utils/kkpPlanningMergeUtils';
import {
  loadPlanningPageState,
  savePlanningPageState,
} from '../utils/kkpPlanningFiltersStorage';
import {
  countPlanningRowsByViewMode,
  ensureDisposCommercialFilterAll,
  filterPlanningRowsByViewMode,
  handlePlanningViewModeChange,
  PLANNING_VIEW_CLIENTS,
  PLANNING_VIEW_DISPOS,
} from '../utils/kkpPlanningViewMode';
import { getPlanningVenteRowId } from '../utils/kkpPlanningFormat';

/**
 * Cycle de vie commun des pages planning ventes KKP.
 *
 * @param {{
 *   storageKey: string,
 *   tableSortColumns: Record<string, object>,
 *   defaultSort?: { key: string, direction?: string },
 *   fetchMeta: () => Promise<object>,
 *   initFiltersFromMeta: (meta: object, savedFilters: object|null) => object,
 *   fetchVentes: (filters: object, ctx: { page: number, viewMode: string }) => Promise<{ ventes: array, pagination: object, viewModeCounts?: object, extras?: object }>,
 *   loadDeps?: array,
 *   getRowId?: (vente: object) => string|number,
 *   viewModeCountsSource?: 'client' | 'api',
 *   filterRows?: (rows: array, filters: object) => array,
 * }} options
 */
export default function usePlanningVentesPage({
  storageKey,
  tableSortColumns,
  defaultSort,
  fetchMeta,
  initFiltersFromMeta,
  fetchVentes,
  loadDeps = [],
  getRowId = getPlanningVenteRowId,
  viewModeCountsSource = 'client',
  filterRows,
}) {
  const { ask, modal } = useAppConfirm();
  const [searchParams] = useSearchParams();
  const [savedPlanningState] = useState(() => loadPlanningPageState(storageKey));
  const [meta, setMeta] = useState(null);
  const [ventes, setVentes] = useState([]);
  const [commerciaux, setCommerciaux] = useState([]);
  const [defaultCommercialId, setDefaultCommercialId] = useState('');
  const [pagination, setPagination] = useState({ page: 1, pages: 0, total: 0 });
  const [filters, setFilters] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingVente, setEditingVente] = useState(null);
  const [modalInitialValues, setModalInitialValues] = useState({});
  const [modalWizardStep, setModalWizardStep] = useState(0);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importStrategy, setImportStrategy] = useState('upsert');
  const [wipeScope, setWipeScope] = useState('all');
  const [listExtras, setListExtras] = useState(null);
  const [apiViewModeCounts, setApiViewModeCounts] = useState(null);
  const fileInputRef = useRef(null);
  const loadRequestSeq = useRef(0);
  const [editingCell, setEditingCell] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const [inlineSaving, setInlineSaving] = useState(false);

  const {
    batValideVente,
    batValideSubmitting,
    batValideError,
    openBatValide,
    closeBatValide,
    submitBatValide,
    statutSelectProps,
  } = useKkpBatValideFlow({
    onUpdated: (updated) => setVentes((prev) => mergePlanningRow(prev, updated)),
  });

  const [viewMode, setViewMode] = useState(() => (
    searchParams.get('vue') === PLANNING_VIEW_DISPOS
      ? PLANNING_VIEW_DISPOS
      : savedPlanningState?.viewMode === PLANNING_VIEW_DISPOS
        ? PLANNING_VIEW_DISPOS
        : PLANNING_VIEW_CLIENTS
  ));

  useEffect(() => {
    ensureDisposCommercialFilterAll(viewMode, filters?.commercial_id, setFilters);
  }, [viewMode, filters?.commercial_id]);

  useSetAssistantPageClient(filters?.search ?? '');

  useEffect(() => {
    if (!filters) return;
    savePlanningPageState(storageKey, { filters, viewMode });
  }, [filters, viewMode, storageKey]);

  const scopedVentes = useMemo(() => {
    if (!filters || typeof filterRows !== 'function') return ventes;
    return filterRows(ventes, filters);
  }, [ventes, filters, filterRows]);

  const visibleVentes = useMemo(
    () => filterPlanningRowsByViewMode(scopedVentes, viewMode),
    [scopedVentes, viewMode],
  );

  const viewModeCounts = useMemo(() => {
    if (viewModeCountsSource === 'api' && apiViewModeCounts) {
      return apiViewModeCounts;
    }
    return countPlanningRowsByViewMode(scopedVentes);
  }, [viewModeCountsSource, apiViewModeCounts, scopedVentes]);

  const { sortedRows, sort, toggleSort, getSortIcon } = useTableSort(
    visibleVentes,
    tableSortColumns,
    defaultSort ? { defaultSort } : {},
  );

  const commercialOptions = useMemo(
    () => commerciaux.map((c) => ({ value: String(c.id), label: c.nom })),
    [commerciaux],
  );

  const loadMeta = useCallback(async () => {
    const data = await fetchMeta();
    setMeta(data);
    setCommerciaux(data.commerciaux ?? []);
    const currentId = data.current_commercial_id ? String(data.current_commercial_id) : '';
    setDefaultCommercialId(currentId);
    setFilters((prev) => prev ?? initFiltersFromMeta(data, savedPlanningState?.filters ?? null));
  }, [fetchMeta, initFiltersFromMeta, savedPlanningState?.filters]);

  const loadVentes = useCallback(async (page = 1) => {
    if (!filters) return;
    const requestSeq = loadRequestSeq.current + 1;
    loadRequestSeq.current = requestSeq;
    setLoading(true);
    setError(null);
    try {
      const result = await fetchVentes(filters, { page, viewMode });
      if (requestSeq !== loadRequestSeq.current) return;
      setVentes(result.ventes ?? []);
      setPagination(result.pagination ?? { page: 1, pages: 0, total: 0 });
      if (result.viewModeCounts) {
        setApiViewModeCounts(result.viewModeCounts);
      }
      setListExtras(result.extras ?? null);
    } catch (err) {
      if (requestSeq !== loadRequestSeq.current) return;
      setError(err.message);
    } finally {
      if (requestSeq === loadRequestSeq.current) {
        setLoading(false);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- loadDeps allows channel-specific deps
  }, [filters, viewMode, fetchVentes, ...loadDeps]);

  useEffect(() => {
    loadMeta().catch((err) => setError(err.message));
  }, [loadMeta]);

  useEffect(() => {
    if (filters) {
      loadVentes(1);
    }
  }, [filters, loadVentes]);

  const openCreate = useCallback((initialValues = {}, wizardStep = 0) => {
    setEditingVente(null);
    setModalWizardStep(wizardStep);
    setModalInitialValues(initialValues);
    setShowModal(true);
  }, []);

  const openEdit = useCallback((vente, wizardStep = 0) => {
    if (!vente?.id) return;
    setEditingVente(vente);
    setModalWizardStep(wizardStep);
    setShowModal(true);
  }, []);

  const closeModal = useCallback(() => setShowModal(false), []);

  const findVenteByRowId = useCallback(
    (rowId) => ventes.find((v) => getRowId(v) === rowId),
    [ventes, getRowId],
  );

  const changeViewMode = useCallback((nextMode) => {
    handlePlanningViewModeChange(nextMode, {
      setViewMode,
      setFilters,
      commercialId: filters?.commercial_id,
    });
  }, [filters?.commercial_id]);

  const importMenuItems = useMemo(() => ([
    {
      key: 'import',
      label: importing ? 'Import…' : 'Importer Excel',
      icon: 'mdi:upload',
      onClick: () => {}, // page overrides via buildImportExportMenu
      disabled: importing,
    },
    {
      key: 'export',
      label: exporting ? 'Export…' : 'Exporter Excel',
      icon: 'mdi:download',
      onClick: () => {},
      disabled: exporting,
    },
  ]), [importing, exporting]);

  const buildImportExportMenu = useCallback((onImportClick, onExport) => ([
    {
      key: 'import',
      label: importing ? 'Import…' : 'Importer Excel',
      icon: 'mdi:upload',
      onClick: onImportClick,
      disabled: importing,
    },
    {
      key: 'export',
      label: exporting ? 'Export…' : 'Exporter Excel',
      icon: 'mdi:download',
      onClick: onExport,
      disabled: exporting,
    },
  ]), [importing, exporting]);

  return {
    ask,
    confirmModal: modal,
    meta,
    setMeta,
    ventes,
    setVentes,
    commerciaux,
    defaultCommercialId,
    pagination,
    filters,
    setFilters,
    loading,
    error,
    setError,
    showModal,
    editingVente,
    modalInitialValues,
    modalWizardStep,
    setModalWizardStep,
    openCreate,
    openEdit,
    closeModal,
    importing,
    setImporting,
    exporting,
    setExporting,
    showImportModal,
    setShowImportModal,
    importStrategy,
    setImportStrategy,
    wipeScope,
    setWipeScope,
    fileInputRef,
    editingCell,
    setEditingCell,
    editDraft,
    setEditDraft,
    inlineSaving,
    setInlineSaving,
    batValideVente,
    batValideSubmitting,
    batValideError,
    openBatValide,
    closeBatValide,
    submitBatValide,
    statutSelectProps,
    viewMode,
    setViewMode,
    changeViewMode,
    viewModeCounts,
    visibleVentes,
    sortedRows,
    sort,
    toggleSort,
    getSortIcon,
    commercialOptions,
    loadMeta,
    loadVentes,
    findVenteByRowId,
    getRowId,
    listExtras,
    savedPlanningState,
    importMenuItems,
    buildImportExportMenu,
  };
}
